// Tech Support Co-Pilot — the inline reply/compose box.
//
// Lets John work the inbox like a normal mail client WITHOUT the agent: write a
// brand-new email, reply (quoted), forward (original files ride along), or edit
// the agent's proposed draft — and attach files. The body is a rich-text TipTap
// editor (bold/italic/underline/lists/links) that submits BOTH body_html and a
// plain-text alternative. Everything is saved as a Gmail DRAFT and sent through
// the same human-send path (POST /mail/draft/{id}/send), so the signature, audit
// row, and the send kill-switch behave identically to the agent's drafts. The
// agent sees whatever John writes because it reads the same live mailbox.

import { useCallback, useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import {
  createMailDraft,
  searchMailRecipients,
  sendMailDraft,
  updateMailDraft,
  uploadMailAttachment,
  type ComposeDraftPayload,
  type MailComposeAttachment,
  type ReplyTemplate,
  type SendDraftResult,
} from '../../services/techSupportCopilot';
import { formatFileSize } from '../../utils/dateFormatters';
import { extractApiErrorMessage } from '../../utils/emailActionUtils';
import EmailAutocompleteInput from '../common/EmailAutocompleteInput';
import type { ComposerInit } from './composerTypes';
import {
  clickedLinkHref,
  extractDraftLinks,
  namePastedFile,
  parseAddressList,
  pastedFiles,
  textToEditorHtml,
  type DraftLink,
  isUnreplyableAddress,
} from './composerUtils';
import TechSupportTemplatePicker from './TechSupportTemplatePicker';
import { trackEvent } from '../../services/portalTracking';
import styles from './TechSupportComposer.module.css';

interface Props {
  init: ComposerInit;
  /** The bound mailbox (techsupport@…) — used to catch a reply addressed to
   *  ourselves before it is sent. */
  accountEmail?: string;
  /** Previewed beneath the body — appended automatically on send. */
  signatureHtml?: string;
  /** Opens the signature editor from the preview — the place John looks for it
   *  while writing a reply (kanban 631e1721). Omitted → preview only. */
  onEditSignature?: () => void;
  onCancel: () => void;
  /** A draft was saved (not sent) — refresh the panel so it shows up. */
  onSaved: () => void;
  /** The draft was sent — the result says delivered / sandbox-redirected / suppressed. */
  onSent: (result: SendDraftResult) => void;
  /** Monotonic counter the mail panel bumps when John presses the 📋 Templates
   *  button it keeps in its fixed header while this composer is open. Each new
   *  value opens the picker; the value present at mount is ignored so a
   *  re-opened composer does not inherit a stale press. */
  openTemplatesSignal?: number;
  /** Save/Send in flight — lets the panel disable its header controls in step
   *  with the composer's own buttons. */
  onBusyChange?: (busy: boolean) => void;
}

/** One attachment row — an upload in flight, or a completed file. */
interface Chip {
  key: string;
  filename: string;
  size_bytes: number;
  uploading?: boolean;
  failed?: boolean;
  done?: MailComposeAttachment;
  /** Object URL thumbnail for image files (pasted snips especially), so John
   *  can SEE what he attached before sending. */
  previewUrl?: string;
}

/** Object-URL thumbnail for an image file (undefined off-browser, e.g. jsdom). */
function imagePreviewUrl(file: File): string | undefined {
  if (!file.type.startsWith('image/')) return undefined;
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return undefined;
  }
  return URL.createObjectURL(file);
}

const MAX_BYTES = 25 * 1024 * 1024;
let _seq = 0;
const nextKey = () => `chip-${_seq++}`;

const TITLES: Record<ComposerInit['mode'], string> = {
  new: '✎ New email',
  reply: '↩ Reply',
  forward: '↪ Forward',
  edit: "✎ Editing the co-pilot's reply",
};

export default function TechSupportComposer({
  init,
  accountEmail,
  signatureHtml,
  onEditSignature,
  onCancel,
  onSaved,
  onSent,
  openTemplatesSignal,
  onBusyChange,
}: Props) {
  const [to, setTo] = useState(init.to ?? '');
  const [cc, setCc] = useState(init.cc ?? '');
  const [bcc, setBcc] = useState(init.bcc ?? '');
  // Cc/Bcc stay tucked away (like Gmail) unless the init already carries them.
  const [showCcBcc, setShowCcBcc] = useState(!!(init.cc || init.bcc));
  const [subject, setSubject] = useState(init.subject ?? '');
  // Plain-text mirror of the editor — drives canSend and the text/plain part.
  const [bodyText, setBodyText] = useState(init.body ?? '');
  const [chips, setChips] = useState<Chip[]>(
    (init.attachments ?? []).map((a) => ({
      key: nextKey(),
      filename: a.filename,
      size_bytes: a.size_bytes,
      done: a,
    })),
  );
  // Files already ON the draft (agent-attached product docs, earlier uploads,
  // forwarded source files). Removable: the surviving list is sent as the
  // update's keep-list, so removing a chip here deletes the file on save.
  const [kept, setKept] = useState<{ filename: string; size: number }[]>(
    init.keptAttachments ?? [],
  );
  const [busy, setBusy] = useState<'save' | 'send' | null>(null);
  useEffect(() => {
    onBusyChange?.(!!busy);
  }, [busy, onBusyChange]);
  const [note, setNote] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // After the first save we have a draft id — keep updating THAT draft so John
  // sees one evolving draft, not many (and Send sends exactly what he saved).
  const draftIdRef = useRef<string | null>(init.draftId ?? null);

  // Only trust the editor's selection once John has actually put his cursor in
  // it — an untouched editor's selection sits at position 0, which would
  // silently PREPEND a template into a prefilled body.
  const hasCursorRef = useRef(false);

  // Every link currently in the draft — anchors AND bare text URLs — so John
  // can TEST them before sending (links inside the editor aren't clickable
  // while editing). Seeded from the initial body; onUpdate keeps it live.
  const [draftLinks, setDraftLinks] = useState<DraftLink[]>(() =>
    extractDraftLinks(init.bodyHtml ?? textToEditorHtml(init.body ?? '')),
  );

  // editorProps are captured ONCE when the editor is created — route the
  // paste/drop handlers through a ref so they always see the latest onFiles.
  const pastedFilesRef = useRef<(files: File[]) => void>(() => undefined);
  const droppedFilesRef = useRef<(files: File[]) => void>(() => undefined);

  const editor = useEditor({
    extensions: [
      // StarterKit v3 bundles underline + link; just configure the link.
      StarterKit.configure({ link: { openOnClick: false } }),
      Placeholder.configure({ placeholder: 'Write your message…' }),
    ],
    content: init.bodyHtml ?? textToEditorHtml(init.body ?? ''),
    // Render synchronously — the composer is small and tests run in jsdom.
    immediatelyRender: true,
    editorProps: {
      // Ctrl/Cmd+Click a link to open it in a new tab — a plain click still
      // just places the caret (openOnClick stays off so editing is safe).
      handleClick: (_view, _pos, event) => {
        const href = clickedLinkHref(event.target, event.ctrlKey || event.metaKey);
        if (!href) return false;
        window.open(href, '_blank', 'noopener,noreferrer');
        return true;
      },
      // 📋→📎 A pasted snip is caught HERE, on the editor, before ProseMirror
      // sees a clipboard with no text/html and runs its capture dance (appends
      // a hidden contenteditable, steals focus mid-event, re-reads it 50ms
      // later). Returning true tells ProseMirror the paste is handled, so the
      // event ends cleanly and the file goes straight to the upload path.
      // Text pastes return false and flow through ProseMirror as before.
      handlePaste: (_view, event) => {
        const files = pastedFiles(event.clipboardData);
        if (files.length === 0) return false;
        event.preventDefault();
        pastedFilesRef.current(files);
        return true;
      },
      // Same for a file dragged onto the body (an Explorer image, a saved
      // snip): attach it rather than letting ProseMirror drop it as nothing.
      // `moved` = ProseMirror dragging its own content around — leave that alone.
      handleDrop: (_view, event, _slice, moved) => {
        if (moved) return false;
        const files = pastedFiles(event.dataTransfer);
        if (files.length === 0) return false;
        event.preventDefault();
        droppedFilesRef.current(files);
        return true;
      },
    },
    onUpdate: ({ editor: e }) => {
      setBodyText(e.getText());
      setDraftLinks(extractDraftLinks(e.getHTML()));
    },
    onFocus: () => {
      hasCursorRef.current = true;
    },
  });

  // 📋 Reply templates: the picker inserts the chosen body AT THE CURSOR in the
  // editor (not appended to the end), so John controls where the house language
  // lands. The thread-header Templates button opens the composer with the picker
  // already showing (init.openTemplates); while the composer is open the SAME
  // button stays in the mail panel's fixed header (John: "stay near the top even
  // when we start a reply") and reaches the picker through openTemplatesSignal.
  const [pickerOpen, setPickerOpen] = useState(!!init.openTemplates);
  const seenTemplatesSignalRef = useRef(openTemplatesSignal ?? 0);
  useEffect(() => {
    const sig = openTemplatesSignal ?? 0;
    if (sig === seenTemplatesSignalRef.current) return; // mount value / no change
    seenTemplatesSignalRef.current = sig;
    if (sig > 0) setPickerOpen(true);
  }, [openTemplatesSignal]);

  const insertTemplate = useCallback(
    (t: ReplyTemplate) => {
      if (!editor) return;
      // Fill {name} from the thread's sender (first name) when we know it — John
      // shouldn't have to hand-replace a placeholder the thread already answers.
      // Other fill-ins ({state}/{product}/{invoice}) stay visible for him.
      const firstName = (init.recipientName ?? '').trim().split(/\s+/)[0] || '';
      const text = firstName ? t.body.replaceAll('{name}', firstName) : t.body;
      // Insert at John's cursor once he has placed one; with no cursor yet,
      // land at the END as its own paragraph (never silently prepend).
      const chain = hasCursorRef.current
        ? editor.chain().focus()
        : editor.chain().focus('end');
      chain.insertContent(textToEditorHtml(text)).run();
    },
    [init.recipientName, editor],
  );

  const uploading = chips.some((c) => c.uploading);
  const hasFailed = chips.some((c) => c.failed);
  // A recipient must be VISIBLE before sending, replies included. Replies used
  // to be allowed with an empty To on the strength of server-side derivation —
  // but that derivation is exactly what mis-addressed contact-form threads, and
  // an invisible recipient is one nobody can catch. The panel now prefills the
  // server-resolved address, so what John sees is what gets mailed.
  //
  // …and the address that IS visible still has to be one a person reads. A
  // no-reply box or our own mailbox is caught here so it is flagged before the
  // reply is written; the server refuses the same send with a 422 regardless.
  const addressedToSelf =
    !!accountEmail &&
    parseAddressList(to)[0]?.toLowerCase() === accountEmail.trim().toLowerCase();
  const recipientProblem = !isUnreplyableAddress(to, accountEmail)
    ? null
    : addressedToSelf
      ? 'That is this mailbox — the reply would land right back in this inbox. '
        + 'Use the customer’s own address.'
      : 'That address does not accept replies, so nobody would read this. '
        + 'Use the customer’s own address.';
  const canSend =
    !!bodyText.trim() && !!to.trim() && !recipientProblem && !uploading && !hasFailed;

  const chipsRef = useRef<HTMLDivElement>(null);

  /**
   * Stage + upload files. `origin` drives the feedback: a paste/drop lands the
   * file BELOW the editor, out of John's eyeline (and below the fold on a short
   * viewport), so those say so in the note and scroll the chip row into view —
   * without that a snip that DID attach looks like a paste that was ignored.
   * A failed upload always says why; the chip's small "failed" label alone is
   * indistinguishable from "nothing happened".
   */
  const onFiles = useCallback(
    async (files: FileList | File[] | null, origin: 'picker' | 'paste' | 'drop' = 'picker') => {
      if (!files || files.length === 0) return;
      setNote(null);
      const picked = Array.from(files);
      const staged: Chip[] = picked.map((f) => ({
        key: nextKey(),
        filename: f.name,
        size_bytes: f.size,
        uploading: true,
        previewUrl: imagePreviewUrl(f),
      }));
      setChips((prev) => [...prev, ...staged]);
      if (origin !== 'picker') {
        // Next frame — the chip row may not exist in the DOM yet.
        requestAnimationFrame(() =>
          chipsRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }),
        );
      }

      const failures: string[] = [];
      await Promise.all(
        picked.map(async (file, i) => {
          const key = staged[i].key;
          const fail = (why: string) => {
            failures.push(`${file.name}: ${why}`);
            setChips((prev) =>
              prev.map((c) => (c.key === key ? { ...c, uploading: false, failed: true } : c)),
            );
          };
          if (file.size > MAX_BYTES) {
            fail(`larger than ${formatFileSize(MAX_BYTES)}`);
            return;
          }
          try {
            const done = await uploadMailAttachment(file);
            setChips((prev) =>
              prev.map((c) => (c.key === key ? { ...c, uploading: false, done } : c)),
            );
          } catch (e) {
            fail(extractApiErrorMessage(e, 'upload failed'));
          }
        }),
      );

      const verb = origin === 'paste' ? 'pasted' : origin === 'drop' ? 'dropped' : null;
      if (failures.length > 0) {
        const what = verb ? `Couldn’t attach the ${verb} file` : 'Couldn’t attach';
        setNote(`${what}: ${failures.join('; ')}. Remove the chip and try again.`);
      } else if (verb) {
        const names = picked.map((f) => f.name).join(', ');
        setNote(`📎 ${verb[0].toUpperCase()}${verb.slice(1)} image attached below as ${names} — it goes out as an attachment.`);
      }
    },
    [],
  );

  const removeChip = useCallback(
    (key: string) => {
      const gone = chips.find((c) => c.key === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      setChips((prev) => prev.filter((c) => c.key !== key));
    },
    [chips],
  );

  // 📋→📎 Clipboard paste: a Windows snip (or any copied image/file) pasted
  // ANYWHERE in the composer becomes an attachment via the normal upload path.
  // Pastes INSIDE the body are handled by the editor's own handlePaste above
  // (which preventDefault()s before the event bubbles here — skip those so a
  // snip is not uploaded twice); this catches pastes into To/Cc/Bcc/Subject.
  // Text pastes fall through untouched (no files on the clipboard).
  const onPastedFiles = useCallback(
    (files: File[]) => {
      void onFiles(files.map((f, i) => namePastedFile(f, i)), 'paste');
    },
    [onFiles],
  );
  const onDroppedFiles = useCallback(
    (files: File[]) => {
      void onFiles(files.map((f, i) => namePastedFile(f, i)), 'drop');
    },
    [onFiles],
  );
  pastedFilesRef.current = onPastedFiles;
  droppedFilesRef.current = onDroppedFiles;

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (e.nativeEvent.defaultPrevented) return; // the editor took it
      const files = pastedFiles(e.clipboardData);
      if (files.length === 0) return;
      e.preventDefault(); // it's a file paste — nothing should land as text
      onPastedFiles(files);
    },
    [onPastedFiles],
  );

  const removeKept = useCallback((idx: number) => {
    setKept((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const saveDraft = useCallback(async () => {
    const attachments = chips
      .map((c) => c.done)
      .filter((a): a is MailComposeAttachment => !!a);
    const ccList = parseAddressList(cc);
    const bccList = parseAddressList(bcc);
    const creating = !draftIdRef.current;
    const payload: ComposeDraftPayload = {
      body: editor?.getText() ?? bodyText,
      body_html: editor?.getHTML() ?? null,
      // Autocomplete tees up the next address with a trailing ", " — strip
      // dangling separators so the To header never goes out as "a@x.com,".
      to: to.trim().replace(/[\s,;]+$/, '') || null,
      subject: subject.trim() || null,
      cc: ccList.length ? ccList : null,
      bcc: bccList.length ? bccList : null,
      // A forward starts its OWN conversation — never thread it into the source.
      thread_id: init.mode === 'forward' ? null : (init.threadId ?? null),
      attachments,
      // The source files are attached by the server on the FIRST save only —
      // after that they live on the draft and edits preserve them.
      forward_from_message_id:
        creating && init.forwardFromMessageId ? init.forwardFromMessageId : null,
      // On update, the surviving kept list tells the server which of the
      // draft's existing files to keep — removing a kept chip deletes that
      // file on save. On create there are no existing files yet.
      kept_attachments: creating
        ? null
        : kept.map((k) => ({ filename: k.filename, size: k.size })),
    };
    const summary = draftIdRef.current
      ? await updateMailDraft(draftIdRef.current, payload)
      : await createMailDraft(payload);
    draftIdRef.current = summary.draft_id;
    // The response carries the draft's authoritative file list: fold completed
    // upload chips into the kept set (they now live ON the draft, so the next
    // save must not re-attach them) and pick up files the server added
    // (e.g. forwarded source files), so the next keep-list covers everything.
    setKept(
      (summary.attachments ?? []).map((a) => ({
        filename: a.filename,
        size: a.size,
      })),
    );
    // Completed chips fold into the kept list — release their thumbnails.
    for (const c of chips) {
      if (c.done && c.previewUrl) URL.revokeObjectURL(c.previewUrl);
    }
    setChips((prev) => prev.filter((c) => !c.done));
    return summary;
  }, [editor, bodyText, to, cc, bcc, subject, chips, kept, init.threadId, init.mode, init.forwardFromMessageId]);

  const onSaveClick = useCallback(async () => {
    if (busy) return;
    setBusy('save');
    setNote(null);
    try {
      await saveDraft();
      setNote('Saved to drafts ✓');
      onSaved();
    } catch (e) {
      // Surface the server's reason — especially the retryable 503 "Gmail is busy,
      // try again in a moment" — instead of a dead-end generic note.
      setNote(extractApiErrorMessage(e, 'Could not save the draft.'));
    } finally {
      setBusy(null);
    }
  }, [busy, saveDraft, onSaved]);

  const onSendClick = useCallback(async () => {
    if (busy || !canSend) return;
    // A reply into a thread derives its "Re: …" subject server-side; anything
    // else with a blank subject goes out as "(no subject)" — the 2026-08-10
    // report's "they have no idea what the subject is". Make that deliberate.
    const subjectDerivedServerSide = !!init.threadId && init.mode !== 'forward';
    if (
      !subject.trim() &&
      !subjectDerivedServerSide &&
      !window.confirm('Send without a subject?')
    ) {
      return;
    }
    setBusy('send');
    setNote(null);
    try {
      const draft = await saveDraft();
      const r = await sendMailDraft(draft.draft_id);
      onSaved(); // the draft now exists / changed — refresh either way
      onSent(r);
    } catch (e) {
      setNote(extractApiErrorMessage(e, 'Could not send.'));
      setBusy(null);
    }
  }, [busy, canSend, saveDraft, onSaved, onSent]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev ?? 'https://');
    if (url === null) return; // cancelled
    if (!url.trim()) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url.trim() }).run();
  }, [editor]);

  const fmtBtn = (
    label: string,
    title: string,
    active: boolean,
    onClick: () => void,
  ) => (
    <button
      type="button"
      className={`${styles.fmtBtn} ${active ? styles.fmtBtnOn : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()} // keep the editor selection
      onClick={onClick}
      data-action="tsc_composer_format"
      data-record-value="format"
      value={title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.composer} aria-label="Compose" onPaste={onPaste}>
      <div className={styles.head}>
        <span className={styles.title}>{TITLES[init.mode]}</span>
        <button
          type="button"
          className={styles.close}
          onClick={onCancel}
          disabled={!!busy}
          aria-label="Close composer"
          data-action="tsc_composer_close"
          data-outcome="close"
        >
          ✕
        </button>
      </div>

      <label className={styles.field}>
        <span className={styles.label}>To</span>
        <EmailAutocompleteInput
          className={styles.input}
          value={to}
          onChange={setTo}
          search={searchMailRecipients}
          ariaLabel="To"
          // Replies no longer derive a hidden recipient — the panel prefills
          // the server-resolved address and it must be visible to send. An
          // empty box on a reply therefore means "nothing replyable was found",
          // so prompt for one rather than promising a derivation.
          placeholder="recipient@example.com"
        />
        {!showCcBcc && (
          <button
            type="button"
            className={styles.ccToggle}
            onClick={() => setShowCcBcc(true)}
            title="Add Cc / Bcc recipients"
            data-action="tsc_composer_cc_toggle"
            data-outcome="open"
          >
            Cc Bcc
          </button>
        )}
      </label>
      {showCcBcc && (
        <>
          <label className={styles.field}>
            <span className={styles.label}>Cc</span>
            <EmailAutocompleteInput
              className={styles.input}
              value={cc}
              onChange={setCc}
              search={searchMailRecipients}
              ariaLabel="Cc"
              placeholder="cc@example.com, another@example.com"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Bcc</span>
            <EmailAutocompleteInput
              className={styles.input}
              value={bcc}
              onChange={setBcc}
              search={searchMailRecipients}
              ariaLabel="Bcc"
              placeholder="bcc@example.com"
            />
          </label>
        </>
      )}
      {recipientProblem && (
        <p className={styles.recipientProblem} role="alert">
          ⚠ {recipientProblem}
        </p>
      )}

      <label className={styles.field}>
        <span className={styles.label}>Subject</span>
        <input
          className={styles.input}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
        />
      </label>

      {/* Formatting only. 📋 Templates is NOT here any more: it lives in the mail
          panel's fixed header while the composer is open (John, 2026-10-06: "stay
          near the top even when we start a reply"), so it never scrolls away with
          the editor and sits exactly where the thread view shows it. */}
      <div className={styles.toolbar} role="toolbar" aria-label="Formatting">
        {editor && (
          <>
            {fmtBtn('B', 'Bold', editor.isActive('bold'), () =>
              editor.chain().focus().toggleBold().run(),
            )}
            {fmtBtn('I', 'Italic', editor.isActive('italic'), () =>
              editor.chain().focus().toggleItalic().run(),
            )}
            {fmtBtn('U', 'Underline', editor.isActive('underline'), () =>
              editor.chain().focus().toggleUnderline().run(),
            )}
            {fmtBtn('•', 'Bullet list', editor.isActive('bulletList'), () =>
              editor.chain().focus().toggleBulletList().run(),
            )}
            {fmtBtn('1.', 'Numbered list', editor.isActive('orderedList'), () =>
              editor.chain().focus().toggleOrderedList().run(),
            )}
            {fmtBtn('🔗', 'Insert link', editor.isActive('link'), setLink)}
          </>
        )}
      </div>
      <div className={styles.editorWrap}>
        <EditorContent editor={editor} className={styles.editor} />
      </div>

      {draftLinks.length > 0 && (
        <div className={styles.linkTest} aria-label="Test the links in this draft">
          <span className={styles.linkTestLabel}>
            🔗 Test links — each opens in a new tab (Ctrl+Click works in the
            editor too):
          </span>
          <span className={styles.linkTestChips}>
            {draftLinks.map((l) => (
              <a
                key={l.href}
                className={styles.linkChip}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open ${l.href} in a new tab`}
                data-action="tsc_composer_link_test"
              >
                {l.label} ↗
              </a>
            ))}
          </span>
        </div>
      )}

      {kept.length > 0 && (
        <div className={styles.chips}>
          {kept.map((a, i) => (
            <span
              key={`kept-${a.filename}-${a.size}-${i}`}
              className={`${styles.chip} ${styles.chipKept}`}
            >
              <span className={styles.chipIcon}>📎</span>
              <span className={styles.chipMeta}>
                <span className={styles.chipName}>{a.filename}</span>
                <span className={styles.chipSize}>{formatFileSize(a.size)} · on draft</span>
              </span>
              <span
                className={styles.chipX}
                role="button"
                aria-label={`Remove ${a.filename}`}
                title="Removed from the draft when you save or send"
                onClick={() => removeKept(i)}
                data-action="tsc_composer_attachment_remove"
              >
                ✕
              </span>
            </span>
          ))}
        </div>
      )}

      {init.mode === 'forward' && init.forwardFromMessageId && (
        <div className={styles.fwdNote}>
          📎 The original message's attachments are included automatically.
        </div>
      )}

      {chips.length > 0 && (
        <div className={styles.chips} ref={chipsRef}>
          {chips.map((c) => (
            <span
              key={c.key}
              className={`${styles.chip} ${c.failed ? styles.chipFailed : ''}`}
            >
              {c.previewUrl ? (
                <img
                  className={styles.chipThumb}
                  src={c.previewUrl}
                  alt={`Preview of ${c.filename}`}
                />
              ) : (
                <span className={styles.chipIcon}>
                  {c.uploading ? '…' : c.failed ? '!' : '📎'}
                </span>
              )}
              <span className={styles.chipMeta}>
                <span className={styles.chipName}>{c.filename}</span>
                <span className={styles.chipSize}>
                  {c.uploading
                    ? 'uploading…'
                    : c.failed
                      ? 'failed'
                      : formatFileSize(c.size_bytes)}
                </span>
              </span>
              <span
                className={styles.chipX}
                role="button"
                aria-label={`Remove ${c.filename}`}
                onClick={() => removeChip(c.key)}
                data-action="tsc_composer_attachment_remove"
              >
                ✕
              </span>
            </span>
          ))}
        </div>
      )}

      {signatureHtml && (
        <div className={styles.sig}>
          <div className={styles.sigLabel}>
            <span>Your signature (added automatically on send)</span>
            {onEditSignature && (
              <button
                type="button"
                className={styles.sigEdit}
                onClick={() => {
                  trackEvent('click', 'tsc_signature_edit', {
                    source: 'composer',
                    composer_mode: init.mode,
                    thread_id: init.threadId ?? null,
                  });
                  onEditSignature();
                }}
                title="Change the sign-off added to your replies (name, title, phone…)"
                data-action="tsc_signature_edit"
                data-outcome="open"
                data-record-value="source"
                value="composer"
              >
                ✎ Edit signature
              </button>
            )}
          </div>
          <div dangerouslySetInnerHTML={{ __html: signatureHtml }} />
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        className={styles.hiddenFile}
        onChange={(e) => {
          void onFiles(e.target.files);
          e.target.value = ''; // allow re-picking the same file
        }}
      />

      <div className={styles.bar}>
        <button
          type="button"
          className={styles.attach}
          onClick={() => fileRef.current?.click()}
          disabled={!!busy}
          data-action="tsc_composer_attach"
          data-outcome="open"
        >
          📎 Attach file
        </button>
        <div className={styles.spacer} />
        {/* The note sits BESIDE the buttons — after "Saved to drafts ✓" John can
            still keep editing and hit Send (previously the note replaced the
            buttons and stranded the composer). */}
        {note && <span className={styles.note}>{note}</span>}
        <button
          type="button"
          className={styles.saveBtn}
          onClick={() => void onSaveClick()}
          disabled={!!busy || uploading}
          data-action="tsc_composer_save"
          data-outcome="request"
          data-target-thread-id={init.threadId ?? undefined}
          data-target-draft-id={init.draftId ?? undefined}
        >
          {busy === 'save' ? 'Saving…' : 'Save draft'}
        </button>
        <button
          type="button"
          className={styles.sendBtn}
          onClick={() => void onSendClick()}
          disabled={!canSend || !!busy}
          title={canSend ? 'Send' : 'Add a recipient and a message first'}
          data-action="tsc_mail_send"
          data-outcome="request"
          data-record-value="source"
          value={`composer_${init.mode}`}
          data-target-thread-id={init.threadId ?? undefined}
          data-target-draft-id={init.draftId ?? undefined}
        >
          {busy === 'send' ? 'Sending…' : 'Send'}
        </button>
      </div>

      <TechSupportTemplatePicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={insertTemplate}
      />
    </div>
  );
}
